import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AccountService } from './account.service';
import { EnvironmentService } from './environment.service';

import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

interface SignalRConnectionInfo {
  url: string;
  accessToken: string;
}

interface SignalRCompatibleConnectionInfo extends SignalRConnectionInfo {
  endpoint: string;
  accessKey: string;
}

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private connection: HubConnection;

  constructor(private httpClient: HttpClient, private accountService: AccountService, private environmentService: EnvironmentService) {}

  public connect(methods: Map<string, (...args: unknown[]) => void>): Promise<boolean> {
    if (!this.accountService.isLoggedIn()) {
      return Promise.resolve(false);
    }

    const userId = this.accountService.userDetails.userId;
    const headers = new HttpHeaders().set('x-ms-signalr-userid', userId);

    return new Promise((resolve, reject) => {
      this.httpClient.post<SignalRCompatibleConnectionInfo>(this.environmentService.signalRNegotiateUrl, null, { headers }).subscribe({
        next: (connInfo: SignalRCompatibleConnectionInfo) => {
          const connectionInfo = {
            accessToken: connInfo.accessToken || connInfo.accessKey,
            url: connInfo.url || connInfo.endpoint,
          };

          const options = {
            accessTokenFactory: () => connectionInfo.accessToken,
          };

          this.connection = new HubConnectionBuilder()
            .withUrl(connectionInfo.url, options)
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Debug)
            .build();

          for (const [methodName, methodCallback] of methods.entries()) {
            this.connection.on(methodName, methodCallback);
          }

          this.connection.onclose(() => console.log('[SignalR] disconnected'));

          this.connection
            .start()
            .then(() => {
              console.log('[SignalR] Connected');
              return resolve(true);
            })
            .catch((error: unknown) => reject(error));
        },
        error: (error: HttpErrorResponse) => {
          reject(error);
        },
      });
    });
  }

  public disconnect(): Promise<void> {
    if (this.connection) {
      return this.connection.stop();
    }
  }
}
